/**
 * @component Layout
 * @description Componente principal del examen EPST. Orquesta el flujo del examen mediante una estructura tipo wizard.
 * Administra los pasos del examen, carga preguntas, maneja las respuestas y muestra componentes como bienvenida,
 * formularios de preguntas, descansos y finalización.
 *
 * @param {Object} props
 * @param {string} props.variant - Tipo de navegación visual para los pasos del wizard.
 * @param {boolean} props.progressBar - Indica si se debe mostrar la barra de progreso.
 * @param {Object} props.certification - Datos de la certificación en curso.
 *
 * @returns {JSX.Element} Interfaz del examen tipo wizard.
 *
 * @example
 * <Layout variant="pills" progressBar={true} certification={certificationData} />
 */

import React, { useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Card, Form } from 'react-bootstrap';

import Welcome from '../welcome/Welcome';
import SectionForm from '../section-form/SectionForm';
import Break from '../break/Break';
import Success from '../success/Success';

import { epstSteps } from 'data/certifications/epstSteps';

import useEpstLifecycle from 'hooks/certification/epst/useEpstLifecycle';
import useEpstQuestionsLoader from 'hooks/certification/epst/useEpstQuestionsLoader';
import useEpstSubmission from 'hooks/certification/epst/useEpstSubmission';

import LayoutHeader from './LayoutHeader';
import LayoutFooter from './LayoutFooter';
import LayoutBody from './LayoutBody';

const Layout = ({ variant, progressBar, certification }) => {
	const dispatch = useDispatch();
	const token = useSelector(state => state.authentication.user?.token);
	const { step, timeOver } = useSelector(state => state.certificationReducer);

	const [questions, setQuestions] = useState(null);
	const [currentQuestion, setCurrentQuestion] = useState({});
	const [selectedAnswer, setSelectedAnswer] = useState({});
	const [loading, setLoading] = useState(false);
	const [blocking, setBlocking] = useState(false);

	const sectionFormRef = useRef(null);

	// Maneja el envío de respuestas, audio y validaciones
	const { handleSubmitAnswer, handleSubmitNoAnswer } = useEpstSubmission({
		step,
		token,
		certification,
		selectedAnswer,
		currentQuestion,
		setSelectedAnswer,
		setQuestions,
		setBlocking,
		sectionFormRef,
		dispatch,
		setLoading
	});

	// Carga las preguntas de la sección correspondiente
	useEpstQuestionsLoader({
		step,
		setLoading,
		setQuestions,
		token,
		certification
	});

	// Maneja el ciclo de vida: temporizador, transiciones y validaciones por paso
	useEpstLifecycle({
		step,
		timeOver,
		selectedAnswer,
		questions,
		setCurrentQuestion,
		dispatch,
		handleSubmitAnswer,
		handleSubmitNoAnswer
	});

	/**
	 * Renderiza el componente correspondiente al paso actual del examen.
	 * @returns {JSX.Element|null}
	 */
	const renderStepComponent = () => {
		if (step === 1) return <Welcome certification={certification} />;
		if (step === 11) return <Success certification={certification} />;
		if ([2, 4, 6, 8, 10].includes(step)) {
			return (
				<SectionForm
					key={currentQuestion?.question_id}
					currentQuestion={currentQuestion}
					onSelectAnswer={setSelectedAnswer}
					sectionId={step}
					ref={sectionFormRef}
				/>
			);
		}
		if ([3, 5, 7, 9].includes(step)) return <Break />;
		return null;
	};

	return (
		<Card as={Form} noValidate className="theme-wizard mb-5">
			<LayoutHeader variant={variant} step={step} steps={epstSteps} />
			<LayoutBody
				progressBar={progressBar}
				step={step}
				loading={loading}
				renderStepComponent={renderStepComponent}
			/>
			<LayoutFooter
				step={step}
				blocking={blocking}
				handleSubmitAnswer={handleSubmitAnswer}
			/>
		</Card>
	);
};

export default Layout;